import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { getImageUrl } from "@/lib/api";
import { normalizeNumberInput, sanitizeLeadingZeroInput } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Package, ChevronLeft, ChevronRight, Upload, X, Download } from "lucide-react";


const getDeleteMessage = (response, fallback) => {
  const data = response?.data || {};
  if (data.action === "soft_deleted") return data.message || fallback;
  return data.message || fallback;
};

export default function AdminBrands() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", logo_url: "", description: "", address: "", phone: "", central_telegram_url: "", discount_agent: 0, discount_agent_type: "percent", discount_agent_value: 0, discount_reseller: 0, discount_reseller_type: "percent", discount_reseller_value: 0, discount_distributor: 0, discount_distributor_type: "percent", discount_distributor_value: 0, discount_produsen: 0, discount_produsen_type: "percent", discount_produsen_value: 0, discount_mitra: 0, discount_mitra_type: "percent", discount_mitra_value: 0 });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [importingBrands, setImportingBrands] = useState(false);
  const brandImportRef = useRef(null);
  const { t, locale } = useLanguage();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchBrands = () => API.get("/brands").then((r) => setBrands(r.data.brands)).catch(() => {});
  const fetchProducts = () => API.get("/products?limit=500").then((r) => setAllProducts(r.data.products || [])).catch(() => {});
  useEffect(() => { fetchBrands(); fetchProducts(); }, []);

  const filteredBrands = brands.filter((b) => 
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (b.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (b.address || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (b.phone || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (b.central_telegram_url || "").toLowerCase().includes(searchQuery.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredBrands.length / itemsPerPage));
  const paginatedBrands = filteredBrands.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const openCreate = () => { setEditing(null); setForm({ name: "", logo_url: "", description: "", address: "", phone: "", central_telegram_url: "", discount_agent: 0, discount_agent_type: "percent", discount_agent_value: 0, discount_reseller: 0, discount_reseller_type: "percent", discount_reseller_value: 0, discount_distributor: 0, discount_distributor_type: "percent", discount_distributor_value: 0, discount_produsen: 0, discount_produsen_type: "percent", discount_produsen_value: 0, discount_mitra: 0, discount_mitra_type: "percent", discount_mitra_value: 0 }); setOpen(true); };
  const openEdit = (b) => { setEditing(b.id); setForm({ name: b.name, logo_url: b.logo_url || "", description: b.description || "", address: b.address || "", phone: b.phone || "", central_telegram_url: b.central_telegram_url || "", discount_agent: b.discount_agent || 0, discount_agent_type: b.discount_agent_type || "percent", discount_agent_value: b.discount_agent_value ?? b.discount_agent ?? 0, discount_reseller: b.discount_reseller || 0, discount_reseller_type: b.discount_reseller_type || "percent", discount_reseller_value: b.discount_reseller_value ?? b.discount_reseller ?? 0, discount_distributor: b.discount_distributor || 0, discount_distributor_type: b.discount_distributor_type || "percent", discount_distributor_value: b.discount_distributor_value ?? b.discount_distributor ?? 0, discount_mitra: b.discount_mitra ?? b.discount_produsen ?? 0, discount_mitra_type: b.discount_mitra_type || b.discount_produsen_type || "percent", discount_mitra_value: b.discount_mitra_value ?? b.discount_mitra ?? b.discount_produsen_value ?? b.discount_produsen ?? 0, discount_produsen: b.discount_produsen || 0, discount_produsen_type: b.discount_produsen_type || "percent", discount_produsen_value: b.discount_produsen_value ?? b.discount_produsen ?? 0 }); setOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) {
        await API.put(`/brands/${editing}`, form);
        toast.success(t("common.saved") || "Brand updated!");
      } else {
        await API.post("/brands", form);
        toast.success(t("common.saved") || "Brand created!");
      }
      setOpen(false);
      fetchBrands();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      const response = await API.delete(`/brands/${id}`);
      toast.success(getDeleteMessage(response, t("admin_products.deleted") || "Brand deleted"));
      fetchBrands();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("scope", "brand-logo");
      const { data } = await API.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, logo_url: data.url || data.path }));
      toast.success(locale === "id" ? "Logo berhasil diupload" : "Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || (locale === "id" ? "Gagal upload logo" : "Failed to upload logo"));
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleDownloadBrandTemplate = async () => {
    try {
      const response = await API.get("/admin/import/brands/template", { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "brands_import_template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal download template brand");
    }
  };

  const handleBrandImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingBrands(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await API.post("/admin/import/brands", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const created = Number(res.data?.created_count || 0);
      const updated = Number(res.data?.updated_count || 0);
      const skipped = Number(res.data?.skipped_count || 0);
      toast.success(`Import brand berhasil: ${created} baru, ${updated} update${skipped ? `, ${skipped} skip` : ""}`);
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal import brand");
    } finally {
      setImportingBrands(false);
      e.target.value = "";
    }
  };

  return (
    <AdminLayout title={t("admin_nav.brands")}>
      <div data-testid="admin-brands">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <p className="text-sm text-stone-500 whitespace-nowrap">{filteredBrands.length} {t("admin_nav.brands").toLowerCase()}</p>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              ref={brandImportRef}
              onChange={handleBrandImport}
              disabled={importingBrands}
            />
            <button
              type="button"
              onClick={handleDownloadBrandTemplate}
              className="bg-white border border-stone-200 text-stone-600 px-4 h-9 text-sm flex items-center justify-center gap-2 hover:bg-stone-50 rounded-md transition-colors whitespace-nowrap"
            >
              <Download className="w-4 h-4" /> Template Brand
            </button>
            <button
              type="button"
              onClick={() => brandImportRef.current?.click()}
              disabled={importingBrands}
              className="bg-white border border-stone-200 text-stone-600 px-4 h-9 text-sm flex items-center justify-center gap-2 hover:bg-stone-50 rounded-md transition-colors whitespace-nowrap disabled:opacity-50"
            >
              <Upload className="w-4 h-4" /> {importingBrands ? "Importing..." : "Upload Batch Brand"}
            </button>
            <div className="relative relative flex-1 sm:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-stone-400" />
              </div>
              <Input
                type="text"
                placeholder={t("common.search_brands")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <button onClick={openCreate} className="bg-[#8A9A86] text-white px-4 h-9 text-sm flex items-center justify-center gap-2 hover:bg-[#768573] rounded-md transition-colors whitespace-nowrap" data-testid="add-brand-btn">
              <Plus className="w-4 h-4" /> {t("admin_brands.add_brand")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {paginatedBrands.map((b) => (
            <div key={b.id} className="bg-white border border-stone-100 p-6 flex flex-col justify-between" data-testid={`brand-row-${b.id}`}>
              <div>
                <div className="flex justify-between items-start mb-3 gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {b.logo_url && <img src={getImageUrl(b.logo_url)} alt={b.name} className="w-10 h-10 object-cover rounded border border-stone-100 shrink-0" />}
                    <h3 className="font-serif text-lg text-stone-900 break-words flex-1 min-w-0">{b.name}</h3>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openEdit(b)} className="text-stone-400 hover:text-stone-700 p-1"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(b.id)} className="text-stone-400 hover:text-red-500 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <p className="text-sm text-stone-500 mb-2 break-words">{b.description || t("admin_brands.no_desc")}</p>
                {b.address && <p className="text-xs text-stone-400 mb-1 break-words">{b.address}</p>}
                {b.phone && <p className="text-xs text-stone-400 mb-1 break-words">{b.phone}</p>}
                {b.central_telegram_url && <p className="text-xs text-stone-400 mb-3 break-words">Telegram: {b.central_telegram_url}</p>}
                <div className="mb-6">
                  <span className="text-[10px] font-semibold tracking-wide uppercase text-stone-600 bg-stone-100 px-2 py-1 rounded inline-flex items-center gap-1.5">
                    <Package className="w-3 h-3 text-stone-400" /> {allProducts.filter(p => p.brand_id === b.id).length} {t("common.products")}
                  </span>
                </div>
              </div>
              <button
                onClick={() => navigate(`/admin/products?brand_id=${b.id}`)}
                className="w-full flex items-center justify-center gap-2 border border-[#8A9A86] text-[#8A9A86] hover:bg-[#8A9A86] hover:text-white transition-colors py-2 rounded text-sm"
              >
                <Package className="w-4 h-4" /> {t("admin_brands.manage_products")}
              </button>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-stone-100 pt-4 mt-6">
            <p className="text-sm text-stone-500">{t("common.page")} {currentPage} {t("common.of")} {totalPages}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? t("admin_brands.edit_brand") : t("admin_brands.new_brand")}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="brand-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Logo Brand</Label>
                <div className="mt-1 flex items-center gap-3">
                  {form.logo_url ? (
                    <div className="relative w-16 h-16 border border-stone-200 rounded overflow-hidden bg-stone-50 shrink-0">
                      <img src={getImageUrl(form.logo_url)} alt="Logo brand" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setForm({ ...form, logo_url: "" })} className="absolute top-0 right-0 bg-red-500 text-white w-5 h-5 flex items-center justify-center"><X className="w-3 h-3" /></button>
                    </div>
                  ) : null}
                  <label className="flex-1 border-2 border-dashed border-stone-200 rounded-md px-3 py-3 flex items-center justify-center gap-2 cursor-pointer hover:border-[#8A9A86] text-sm text-stone-500">
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    <Upload className="w-4 h-4" /> {uploadingLogo ? "Uploading..." : "Upload Logo"}
                  </label>
                </div>
                <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="mt-2" placeholder="URL/path logo otomatis terisi setelah upload" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.desc")}</Label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                  className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 focus:outline-none focus:border-[#8A9A86]" data-testid="brand-desc-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Domisili / Alamat Brand</Label>
                <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={3}
                  className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 focus:outline-none focus:border-[#8A9A86]" data-testid="brand-address-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Nomor Telepon Brand</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="brand-phone-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Link Telegram Pusat</Label>
                <Input value={form.central_telegram_url} onChange={(e) => setForm({ ...form, central_telegram_url: e.target.value })} className="mt-1" placeholder="https://t.me/..." data-testid="brand-central-telegram-input" />
              </div>
              <button onClick={handleSave} className="w-full bg-[#8A9A86] text-white py-3 text-sm uppercase tracking-wider hover:bg-[#768573]" data-testid="save-brand-btn">
                {editing ? t("admin_brands.update_btn") : t("admin_brands.create_btn")}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
